import { formatMessage, msg } from "./i18n/translate";
/**
 * 班級商店與成長徽章的領域定義。
 * 學生不登入：由老師代為兌換，點數立即凍結（pending），拒絕時退回。
 * 徽章定義放代碼（隨產品演進），資料庫只保存班級實際達成的時間。
 */

export interface DefaultStoreItem {
  name: string;
  emoji: string;
  points: number;
}

/** 常用獎勵預設組（客戶調研中的免作業券等高頻 SKU），老師一鍵匯入後可改。 */
export const DEFAULT_STORE_ITEMS: DefaultStoreItem[] = [
  { name: msg("免作業券"), emoji: "📝", points: 20 },
  { name: msg("自選座位一日"), emoji: "🪑", points: 15 },
  { name: msg("班長一日"), emoji: "👑", points: 30 },
  { name: msg("老師小助手"), emoji: "🤝", points: 10 },
  { name: msg("午休音樂點播"), emoji: "🎵", points: 15 },
  { name: msg("帶玩具分享一日"), emoji: "🧸", points: 10 },
  { name: msg("和好朋友同桌一日"), emoji: "👫", points: 20 },
  { name: msg("班級圖書優先借"), emoji: "📚", points: 8 },
  { name: msg("神秘小禮物"), emoji: "🎁", points: 25 },
  { name: msg("遊戲時間 10 分鐘"), emoji: "🎮", points: 35 },
  { name: msg("自選貼紙一張"), emoji: "🌈", points: 5 },
  { name: msg("幫全班選下節課音樂"), emoji: "🎧", points: 18 },
];

/**
 * 驚喜獎勵：商品可設成盲盒、扭蛋或刮刮卡，老師代兌換時由伺服器從獎品池抽出一項。
 * 仍用同一套目前點數；出現機會只分三級，老師不必自己算機率。三種只差揭曉畫面（D-250 盲盒）。
 */
export type RewardKind = "standard" | "blindbox" | "capsule" | "scratch";
export type SurpriseKind = Exclude<RewardKind, "standard">;
export type PrizeRarity = "common" | "uncommon" | "rare";

export interface SurprisePrize {
  id: string;
  name: string;
  emoji: string | null;
  rarity: PrizeRarity;
  /** Prizes left (customer batch §3, kept in prizes_json); absent = unlimited. A prize at 0 can no longer be drawn. */
  quantity?: number;
}
/** Highest remaining count a teacher may set; a rejected redemption never pushes a prize past it. */
export const PRIZE_QUANTITY_MAX = 999;

export const REWARD_KINDS: RewardKind[] = ["standard", "blindbox", "capsule", "scratch"];
export const SURPRISE_KINDS: SurpriseKind[] = ["blindbox", "capsule", "scratch"];
export const REWARD_KIND_LABELS: Record<RewardKind, string> = { standard: msg("一般獎勵"), blindbox: msg("盲盒"), capsule: msg("扭蛋"), scratch: msg("刮刮卡") };
export const PRIZE_RARITIES: PrizeRarity[] = ["common", "uncommon", "rare"];
export const PRIZE_RARITY_LABELS: Record<PrizeRarity, string> = { common: msg("常見"), uncommon: msg("少見"), rare: msg("稀有") };
export const PRIZE_RARITY_WEIGHTS: Record<PrizeRarity, number> = { common: 6, uncommon: 3, rare: 1 };
export const SURPRISE_PRIZE_MIN = 2;
export const SURPRISE_PRIZE_MAX = 12;
/** Same as an item name: translated prize names (e.g. the default pool in English) run past 20 (D-250). */
export const SURPRISE_PRIZE_NAME_MAX = 40;

/** 新增驚喜獎勵時預填的範例獎品池，老師可再改。 */
export const DEFAULT_SURPRISE_PRIZES: Array<Omit<SurprisePrize, "id">> = [
  { name: msg("自選貼紙一張"), emoji: "🌈", rarity: "common" },
  { name: msg("老師小助手"), emoji: "🤝", rarity: "common" },
  { name: msg("自選座位一日"), emoji: "🪑", rarity: "uncommon" },
  { name: msg("免作業券"), emoji: "📝", rarity: "rare" },
];

/**
 * 盲盒範例 (D-250)：建議獎勵旁的一鍵範例，加入後就是一個普通的盲盒商品，獎品池和點數都能再改。
 * 獎品沿用常用獎勵的名字（已有各語言譯文），老師不必先想獎品。
 */
export const BLIND_BOX_PRESETS: Array<{ key: string; name: string; emoji: string; points: number; prizes: Array<Omit<SurprisePrize, "id">> }> = [
  { key: "blindbox-mystery", name: msg("神秘盲盒"), emoji: "🎁", points: 20, prizes: DEFAULT_SURPRISE_PRIZES },
  { key: "blindbox-lucky", name: msg("幸運小盲盒"), emoji: "🍀", points: 8, prizes: [
    { name: msg("自選貼紙一張"), emoji: "🌈", rarity: "common" },
    { name: msg("老師小助手"), emoji: "🤝", rarity: "common" },
    { name: msg("午休音樂點播"), emoji: "🎵", rarity: "uncommon" },
    { name: msg("班長一日"), emoji: "👑", rarity: "rare" },
  ] },
];

export const isSurpriseKind = (kind: unknown): kind is SurpriseKind => SURPRISE_KINDS.includes(kind as SurpriseKind);

/** message is the Chinese text; `source` + `params` let callers translate it (t(source, params)). */
export class SurprisePrizeError extends Error {
  readonly source: string;
  constructor(readonly code: string, message: string, readonly params?: Record<string, string | number>) {
    super(formatMessage(message, params));
    this.source = message;
  }
}

/** 驗證並正規化獎品池；錯誤以老師看得懂的訊息拋出。 */
export const normalizeSurprisePrizes = (input: unknown, newId: () => string): SurprisePrize[] => {
  if (!Array.isArray(input) || input.length < SURPRISE_PRIZE_MIN || input.length > SURPRISE_PRIZE_MAX)
    throw new SurprisePrizeError("invalid_prize_pool", "驚喜獎勵需要 {min}–{max} 項獎品。", { min: SURPRISE_PRIZE_MIN, max: SURPRISE_PRIZE_MAX });
  const names = new Set<string>();
  const ids = new Set<string>();
  return input.map((raw) => {
    const prize = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const name = typeof prize.name === "string" ? prize.name.trim() : "";
    if (!name || name.length > SURPRISE_PRIZE_NAME_MAX)
      throw new SurprisePrizeError("invalid_prize_name", "每項獎品名稱需 1–{max} 字。", { max: SURPRISE_PRIZE_NAME_MAX });
    if (names.has(name)) throw new SurprisePrizeError("duplicate_prize_name", "獎品「{name}」重複了。", { name });
    names.add(name);
    const emoji = typeof prize.emoji === "string" && prize.emoji.trim() ? prize.emoji.trim() : null;
    if (emoji && emoji.length > 16) throw new SurprisePrizeError("invalid_prize_emoji", "獎品圖示過長。");
    const rarity = PRIZE_RARITIES.includes(prize.rarity as PrizeRarity) ? prize.rarity as PrizeRarity : null;
    if (!rarity) throw new SurprisePrizeError("invalid_prize_rarity", "請為每項獎品選擇出現機會。");
    // Per-prize points are not supported yet (a card costs its own points); refuse rather than store a meaning we have not defined.
    if (prize.cost !== undefined && prize.cost !== null) throw new SurprisePrizeError("prize_cost_unsupported", "獎品暫時不能各自設定扣點；整張卡的點數請在商品上設定。");
    const rawQuantity = prize.quantity;
    const unlimited = rawQuantity === undefined || rawQuantity === null || rawQuantity === "";
    if (!unlimited && (typeof rawQuantity !== "number" || !Number.isInteger(rawQuantity) || rawQuantity < 0 || rawQuantity > PRIZE_QUANTITY_MAX))
      throw new SurprisePrizeError("invalid_prize_quantity", "獎品數量需是 0 到 {max} 的整數，或留空代表不限。", { max: PRIZE_QUANTITY_MAX });
    // Keep an existing id so a later edit still refers to the same prize; ids are never shown.
    let id = typeof prize.id === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(prize.id) ? prize.id : newId();
    if (ids.has(id)) id = newId();
    ids.add(id);
    return unlimited ? { id, name, emoji, rarity } : { id, name, emoji, rarity, quantity: rawQuantity as number };
  });
};

/** 讀取資料庫裡的獎品池；格式壞掉時回傳空陣列，兌換會拒絕而不是亂抽。 */
export const parseSurprisePrizes = (json: string | null | undefined): SurprisePrize[] => {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.filter((prize): prize is SurprisePrize =>
      prize && typeof prize.name === "string" && PRIZE_RARITIES.includes(prize.rarity)).map((prize) =>
      Number.isInteger(prize.quantity) && prize.quantity! >= 0 ? prize : (({ quantity: _quantity, ...rest }) => rest)(prize)) : [];
  } catch { return []; }
};

/** 各獎品的出現機率（百分比，四捨五入到整數，供老師預覽）。 */
export const prizeAvailable = (prize: Pick<SurprisePrize, "quantity">) => prize.quantity === undefined || prize.quantity > 0;
/** Prizes that can still be drawn, and whether a pool has any left. */
export const drawablePrizes = <T extends Pick<SurprisePrize, "quantity">>(prizes: T[]): T[] => prizes.filter(prizeAvailable);
export const prizesExhausted = (prizes: Array<Pick<SurprisePrize, "quantity">>) => prizes.length > 0 && !prizes.some(prizeAvailable);
export const prizeChances = (prizes: Array<Pick<SurprisePrize, "rarity" | "quantity">>): number[] => {
  const total = prizes.reduce((sum, prize) => sum + (prizeAvailable(prize) ? PRIZE_RARITY_WEIGHTS[prize.rarity] : 0), 0);
  return prizes.map((prize) => total && prizeAvailable(prize) ? Math.round(PRIZE_RARITY_WEIGHTS[prize.rarity] * 100 / total) : 0);
};

/** The pool's JSON after a prize was handed out (-1) or put back (+1); unlimited prizes, unknown ids and a pool without that prize come back unchanged (null). */
export function prizesJsonAfter(prizes: SurprisePrize[], prizeId: string, delta: 1 | -1): string | null {
  const index = prizes.findIndex((prize) => prize.id === prizeId);
  const prize = prizes[index];
  if (!prize || prize.quantity === undefined) return null;
  const quantity = Math.min(PRIZE_QUANTITY_MAX, Math.max(0, prize.quantity + delta));
  if (quantity === prize.quantity) return null;
  return JSON.stringify(prizes.map((item, at) => at === index ? { ...item, quantity } : item));
}
/** A short fingerprint of the stored pool, so an edit made from a stale screen can be told apart (and refused) after sales changed the counts. */
export const prizesVersion = (json: string | null | undefined) => {
  let hash = 2166136261;
  for (const ch of json ?? "") hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619) >>> 0;
  return hash.toString(36);
};

/** 依出現機會加權抽一項；random 回傳 [0,1)。 */
export const drawSurprisePrize = <T extends Pick<SurprisePrize, "rarity"> & Partial<Pick<SurprisePrize, "quantity">>>(all: T[], random: () => number): T => {
  if (!all.length) throw new SurprisePrizeError("empty_prize_pool", "這個驚喜獎勵還沒有獎品。");
  const prizes = drawablePrizes(all);
  if (!prizes.length) throw new SurprisePrizeError("prizes_exhausted", "這個驚喜獎勵的獎品都抽完了。");
  const total = prizes.reduce((sum, prize) => sum + PRIZE_RARITY_WEIGHTS[prize.rarity], 0);
  let ticket = Math.min(Math.max(random(), 0), 0.999999999) * total;
  for (const prize of prizes) {
    ticket -= PRIZE_RARITY_WEIGHTS[prize.rarity];
    if (ticket < 0) return prize;
  }
  return prizes[prizes.length - 1];
};

import type { PetMode } from "./pet-modes";

export type BadgeKind = "hatch" | "level" | "actions" | "companion-out" | "companion-evolved" | "companion-half" | "companion-final";

export interface BadgeDefinition {
  key: string;
  name: string;
  emoji: string;
  description: string;
  kind: BadgeKind;
  /** kind=level 時的等級門檻；kind=actions 時的記分次數門檻 */
  threshold: number;
}

/**
 * 具體肯定次數的徽章兩種模式共用；班寵孵化／等級只屬全班共養；
 * 夥伴里程碑只屬一人一寵（看全班各自的寵物出場與進化）。
 */
export const badgeAppliesTo = (kind: BadgeKind, mode: PetMode) =>
  kind === "actions" || (mode === "class" ? kind === "hatch" || kind === "level" : kind.startsWith("companion-"));

export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  { key: "badge-hatched", name: msg("破蛋而出"), emoji: "🐣", description: msg("班寵順利孵化"), kind: "hatch", threshold: 0 },
  { key: "badge-level-5", name: msg("穩定成長"), emoji: "🌱", description: msg("班寵達到等級 5"), kind: "level", threshold: 5 },
  { key: "badge-level-10", name: msg("茁壯夥伴"), emoji: "🌿", description: msg("班寵達到等級 10"), kind: "level", threshold: 10 },
  { key: "badge-level-15", name: msg("森林常客"), emoji: "🍃", description: msg("班寵達到等級 15"), kind: "level", threshold: 15 },
  { key: "badge-level-20", name: msg("可靠同伴"), emoji: "🌳", description: msg("班寵達到等級 20"), kind: "level", threshold: 20 },
  { key: "badge-level-25", name: msg("傳說班寵"), emoji: "🌟", description: msg("班寵達到等級 25"), kind: "level", threshold: 25 },
  { key: "badge-level-30", name: msg("滿級榮光"), emoji: "🏆", description: msg("班寵達到等級 30"), kind: "level", threshold: 30 },
  { key: "badge-actions-50", name: msg("五十次肯定"), emoji: "💝", description: msg("全班累積 50 次具體肯定"), kind: "actions", threshold: 50 },
  { key: "badge-actions-200", name: msg("兩百次肯定"), emoji: "💖", description: msg("全班累積 200 次具體肯定"), kind: "actions", threshold: 200 },
  { key: "badge-actions-500", name: msg("五百次肯定"), emoji: "💫", description: msg("全班累積 500 次具體肯定"), kind: "actions", threshold: 500 },
  { key: "badge-companion-first", name: msg("第一位夥伴"), emoji: "🐣", description: msg("第一隻夥伴從小窩或蛋裡出場"), kind: "companion-out", threshold: 1 },
  { key: "badge-companion-evolved", name: msg("第一次進化"), emoji: "🌿", description: msg("第一隻夥伴進化到第二形態"), kind: "companion-evolved", threshold: 1 },
  { key: "badge-companion-half", name: msg("一起長大"), emoji: "🌳", description: msg("一半同學的夥伴進化到第二形態"), kind: "companion-half", threshold: 0 },
  { key: "badge-companion-final", name: msg("最終形態"), emoji: "🏆", description: msg("第一隻夥伴達到最終形態"), kind: "companion-final", threshold: 1 },
];

export interface BadgeProgress {
  /** 預設 class（舊呼叫只算全班共養的徽章） */
  mode?: PetMode;
  hatched: boolean;
  level: number;
  totalActions: number;
  /** 一人一寵：在冊學生數，以及寵物已出場（Lv≥1）、已到第二形態、已到最終形態的人數 */
  companions?: { total: number; out: number; evolved: number; final: number };
}

/** 依現況判斷哪些徽章定義已達成（尚未寫入資料庫）。 */
export const earnedBadgeKeys = (progress: BadgeProgress): string[] => {
  const mode = progress.mode ?? "class";
  const companions = progress.companions ?? { total: 0, out: 0, evolved: 0, final: 0 };
  return BADGE_DEFINITIONS.filter((badge) => badgeAppliesTo(badge.kind, mode) && (
    badge.kind === "hatch" ? progress.hatched
      : badge.kind === "level" ? progress.level >= badge.threshold
      : badge.kind === "actions" ? progress.totalActions >= badge.threshold
      : badge.kind === "companion-out" ? companions.out >= badge.threshold
      : badge.kind === "companion-evolved" ? companions.evolved >= badge.threshold
      : badge.kind === "companion-final" ? companions.final >= badge.threshold
      : companions.total > 0 && companions.evolved * 2 >= companions.total
  )).map((badge) => badge.key);
};

/** 由一人一寵的寵物狀態統計夥伴里程碑所需的人數。 */
export const companionProgress = (pets: Array<{ status: string; visualStage: number }>, enrolled: number) => ({
  total: enrolled,
  out: pets.filter((pet) => pet.status !== "egg" && pet.visualStage >= 1).length,
  evolved: pets.filter((pet) => pet.status !== "egg" && pet.visualStage >= 10).length,
  final: pets.filter((pet) => pet.status !== "egg" && pet.visualStage >= 30).length,
});

// Current points per student for one class snapshot (redemptions subtracted by the caller).
export const CLASS_POINTS_SQL =
  "SELECT e.student_id, COALESCE(SUM(CASE WHEN a.polarity='negative' THEN -e.points ELSE e.points END),0) p FROM score_entries e JOIN score_actions a ON a.id=e.action_id AND a.undone_at IS NULL WHERE e.workspace_id=? AND e.class_id=? GROUP BY e.student_id";

// The balance and stock checks run inside the same D1 batch as the insert.
// Two overlapping requests therefore cannot both spend the same points.
export const REDEEM_INSERT_SQL = `
INSERT INTO store_redemptions(id,workspace_id,class_id,student_id,item_id,item_name_snapshot,points_spent)
SELECT ?,i.workspace_id,i.class_id,s.id,i.id,i.name,i.points
FROM store_items i JOIN students s ON s.workspace_id=i.workspace_id AND s.class_id=i.class_id
WHERE i.id=? AND i.workspace_id=? AND i.class_id=? AND i.is_active=1
  AND (i.stock IS NULL OR i.stock>0)
  AND s.id=? AND s.archived_at IS NULL
  AND COALESCE((SELECT SUM(CASE WHEN a.polarity='negative' THEN -e.points ELSE e.points END) FROM score_entries e
    JOIN score_actions a ON a.id=e.action_id AND a.undone_at IS NULL
    WHERE e.workspace_id=i.workspace_id AND e.class_id=i.class_id AND e.student_id=s.id),0)
    - COALESCE((SELECT SUM(r.points_spent) FROM student_spend_v r
      WHERE r.workspace_id=i.workspace_id AND r.class_id=i.class_id AND r.student_id=s.id),0) >= i.points`;

/**
 * 兌換寫入只在商品名稱、點數與獎品池仍與先前讀取一致時成立：
 * 回應與抽出的獎品都來自那次讀取，並發編輯會讓插入落空（API 回 409）。
 * 參數在 REDEEM_INSERT_SQL 之後依序為 name、points、prizes_json（一般獎勵傳空字串）。
 */
export const REDEEM_INSERT_UNCHANGED_SQL = `${REDEEM_INSERT_SQL}
  AND i.name=? AND i.points=? AND COALESCE(i.prizes_json,'')=?`;

/** A child's waiting reward request as the teacher's class snapshot lists it (D-135, src/store-requests.ts). */
export type OpenRequest = {
  id: string; student_id: string; student_name: string; item_id: string;
  item_name: string; points: number; created_at: string;
  /** The item no longer matches what the child asked for (renamed, repriced, switched off or sold out). */
  item_changed: boolean; current_points: number | null;
  /** False once the student was transferred out or archived: the request can only be declined. */
  student_enrolled: boolean;
};
